import React, { useMemo, useState } from 'react';
import { Loader2, Upload, Download, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  UploadTaskRow,
  UploadTasksAggregateResult,
  UPLOAD_BATCH_SIZE,
  uploadTasksFromPayloads,
} from '../../../../services/adminCampaigns';
import { TaskTypeModule } from '../../../../taskTypes/module';

interface Props {
  campaignId: string;
  campaignName: string;
  taskType: TaskTypeModule;
  inline?: boolean;
  onClose: () => void;
  onUploaded: (result: UploadTasksAggregateResult) => void;
}

interface ParsedLine {
  lineNumber: number;
  raw: string;
  row?: UploadTaskRow;
  error?: string;
}

/**
 * Parses a JSONL string; empty lines are skipped. Each line is annotated with
 * its 1-based line number so preview rows can point at the source line.
 */
function parseJsonl(text: string, mod: TaskTypeModule): ParsedLine[] {
  const out: ParsedLine[] = [];
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    const lineNumber = i + 1;
    let obj: unknown;
    try {
      obj = JSON.parse(raw);
    } catch (e: any) {
      out.push({ lineNumber, raw, error: `Invalid JSON: ${e.message ?? 'parse error'}` });
      continue;
    }
    const parsed = mod.parsePayloadRow?.(obj);
    if (!parsed) {
      out.push({ lineNumber, raw, error: 'Task type does not accept uploads.' });
      continue;
    }
    if ('error' in parsed) {
      out.push({ lineNumber, raw, error: parsed.error });
      continue;
    }
    out.push({
      lineNumber,
      raw,
      row: {
        taskTitle: parsed.taskTitle,
        payload: parsed.payload,
        ...(parsed.uploadKey ? { uploadKey: parsed.uploadKey } : {}),
      },
    });
  }
  return out;
}

export function UploadTasksModal({
  campaignId,
  campaignName,
  taskType,
  inline = false,
  onClose,
  onUploaded,
}: Props) {
  const schema = taskType.payloadUploadSchema!;
  const [rawText, setRawText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<UploadTasksAggregateResult | null>(null);

  const parsed = useMemo(() => {
    if (!rawText.trim()) return [];
    return parseJsonl(rawText, taskType);
  }, [rawText, taskType]);

  const validRows = parsed.filter((p) => p.row).length;
  const errorRows = parsed.filter((p) => p.error).length;
  const canSubmit = validRows > 0 && errorRows === 0 && !uploading;

  const onFile = async (file: File) => {
    setErr(null);
    setResult(null);
    try {
      const text = await file.text();
      setRawText(text);
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to read file.');
    }
  };

  const onDownloadTemplate = () => {
    const blob = new Blob([schema.sample], { type: 'application/x-ndjson' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${taskType.id}.template.jsonl`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    setErr(null);
    setUploading(true);
    setProgress({ done: 0, total: Math.max(1, Math.ceil(validRows / UPLOAD_BATCH_SIZE)) });
    try {
      const rows = parsed.filter((p) => p.row).map((p) => p.row!);
      const res = await uploadTasksFromPayloads(
        { campaignId, taskTypeId: taskType.id, rows },
        (done, total) => setProgress({ done, total }),
      );
      setResult(res);
      onUploaded(res);
    } catch (e: any) {
      setErr(e?.message ?? 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={inline
        ? 'border-b border-gray-200 py-4 last:border-b-0'
        : 'fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4'}
      role={inline ? undefined : 'dialog'}
      aria-modal={inline ? undefined : true}
      aria-labelledby="upload-tasks-modal-title"
    >
      <div className={inline ? 'w-full' : 'mt-10 w-full max-w-3xl rounded-lg bg-white shadow-xl'}>
        <div className={inline
          ? 'flex items-start justify-between border-b border-gray-200 pb-3'
          : 'flex items-start justify-between border-b border-gray-200 p-4'}>
          <div className="min-w-0">
            <h2
              id="upload-tasks-modal-title"
              className="text-base font-semibold text-gray-900"
            >
              Upload tasks · {taskType.shortName}
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {campaignName} · {taskType.displayName}
            </p>
          </div>
          {!inline && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className={inline ? 'space-y-4 py-4' : 'space-y-4 p-4'}>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
            <p className="mb-1 font-medium">{schema.description}</p>
            <p className="mb-2 text-gray-500">
              Format: {schema.format.toUpperCase()} · Columns:{' '}
              {schema.columns
                .map((c) => `${c.key}${c.required ? '*' : ''}`)
                .join(', ')}
            </p>
            <button
              type="button"
              onClick={onDownloadTemplate}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download template
            </button>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              JSONL content
            </label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="file"
                accept=".jsonl,.json,.txt,application/json,application/x-ndjson,text/plain"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                }}
                className="text-xs text-gray-700"
              />
              <span className="text-xs text-gray-500">
                or paste below. Max {UPLOAD_BATCH_SIZE} rows per batch; large
                files are uploaded in sequence.
              </span>
            </div>
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={10}
              placeholder={schema.sample}
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-xs text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {parsed.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
                <span>
                  {validRows} ready · {errorRows} error{errorRows === 1 ? '' : 's'} ·{' '}
                  {parsed.length} line{parsed.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="max-h-60 overflow-y-auto rounded-md border border-gray-200">
                <table className="min-w-full divide-y divide-gray-100 text-xs">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-2 py-1 font-medium text-gray-500">#</th>
                      <th className="px-2 py-1 font-medium text-gray-500">Status</th>
                      <th className="px-2 py-1 font-medium text-gray-500">Title / error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parsed.map((p) => (
                      <tr key={p.lineNumber} className={p.error ? 'bg-red-50' : ''}>
                        <td className="px-2 py-1 text-gray-500">{p.lineNumber}</td>
                        <td className="px-2 py-1">
                          {p.error ? (
                            <span className="inline-flex items-center gap-1 text-red-700">
                              <AlertTriangle className="h-3 w-3" />
                              error
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="h-3 w-3" />
                              ready
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          {p.error ? (
                            <span className="text-red-700">{p.error}</span>
                          ) : (
                            <span className="text-gray-800">
                              {p.row?.taskTitle}
                              {p.row?.uploadKey && (
                                <span className="ml-2 text-gray-400">
                                  id:{p.row.uploadKey}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {err && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {err}
            </div>
          )}

          {result && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Created {result.created} · Updated {result.updated} · Skipped{' '}
              {result.skipped.length} (new pool size: {result.totalAfter})
              {result.skipped.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px]">
                  {result.skipped.slice(0, 10).map((s, i) => (
                    <li key={i}>
                      Row {s.index + 1}
                      {s.uploadKey ? ` · ${s.uploadKey}` : ''} — {s.reason}
                    </li>
                  ))}
                  {result.skipped.length > 10 && (
                    <li className="list-none text-gray-500">
                      …and {result.skipped.length - 10} more.
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className={inline
          ? 'flex items-center justify-between gap-3 border-t border-gray-200 py-3'
          : 'flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3'}>
          <div className="text-xs text-gray-500">
            {uploading && progress
              ? `Uploading batch ${progress.done}/${progress.total}…`
              : `${validRows} row${validRows === 1 ? '' : 's'} ready`}
          </div>
          <div className="flex items-center gap-2">
            {!inline && (
              <button
                type="button"
                onClick={onClose}
                disabled={uploading}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {result ? 'Close' : 'Cancel'}
              </button>
            )}
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Create tasks
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
